import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { SupportPlanType } from '@kora/contracts';
import styles from './ClientPortalPage.module.css';
import { DashboardLayout } from '@/shared/components/layout/DashboardLayout';
import { Tabs } from '@/shared/components/ui/Tabs';
import { TextField } from '@/shared/components/ui/TextField';
import { KabeliLogo } from '@/shared/components/layout/KabeliLogo';
import { ChevronLeftIcon, SearchIcon } from '@/shared/components/icons/Icons';
import { useClientDetail } from '@/modules/clients/api/clients.queries';
import { useProjectsList } from '@/modules/projects/api/projects.queries';
import { useSupportPlansForClient } from '@/modules/projects/api/support-plan.queries';
import { ClientCompanyHeader } from '../components/ClientCompanyHeader';
import { PortalTable, type PortalTableColumn } from '../components/PortalTable';
import { StatPill } from '../components/StatPill';
import { StatusPill, type StatusPillTone } from '../components/StatusPill';
import { toClientCompanyInfo, toClientProjectRow, toClientSupportContractRow } from '../adapters/portal-list.adapter';
import type { ClientProjectHealth, ClientProjectRow, ClientSupportContractRow } from '../types';
import { formatMoney } from '@/shared/lib/format-money';
import { portalProjectPath, portalSupportContractPath } from '@/app/router/paths';

/**
 * Portal de cliente — tabs "Proyectos Desarrollo" y "Soporte".
 *
 * Sirve las dos rutas `/portal/proyectos` y `/portal/soporte` (ver
 * `app/router/routes.tsx`): en el Figma de referencia son dos entradas de
 * menú independientes que llevan a la misma pantalla con una pestaña
 * distinta activa, así que aquí la pestaña activa se deriva de la URL en vez
 * de guardarse solo en estado local — al revés de como lo hace
 * `DashboardPage` (que sí tiene una única entrada de menú "Dashboard").
 *
 * DATOS: proyectos vía `useProjectsList({ clientId })`, contratos de soporte
 * vía `useSupportPlansForClient(clientId)` — ver `adapters/portal-list.adapter.ts`.
 */

const HEALTH_LABEL: Readonly<Record<ClientProjectHealth, string>> = {
  prospect: 'Prospecto',
  quoted: 'Cotización',
  not_started: 'No iniciado',
  active: 'Activo',
  on_hold: 'Detenido',
  finished: 'Finalizado',
  cancelled: 'Cancelado',
};

const HEALTH_TONE: Readonly<Record<ClientProjectHealth, StatusPillTone>> = {
  prospect: 'info',
  quoted: 'info',
  not_started: 'neutral',
  active: 'success',
  on_hold: 'warning',
  finished: 'neutral',
  cancelled: 'neutral',
};

const PLAN_TYPE_LABEL: Readonly<Record<SupportPlanType, string>> = {
  monthly: 'Mensual',
  bundle: 'Bolsa',
  on_demand: 'A demanda',
};

export interface ClientPortalPageProps {
  /** Empresa cuyos proyectos/contratos se muestran — sin esto, la lista queda vacía. */
  readonly clientId?: string;
  /** Rutas reales del portal por defecto. La vista previa de admin
   *  (`ClientPortalPreviewPage`) las reemplaza por `/clientes/:clientId/vista-cliente[...]`,
   *  ya que un admin/PMO no tiene el permiso `projects:read_own_client` que
   *  protege `/portal/*` a propósito — ver `docs/DECISION-PORTAL-CLIENTE.md`. */
  readonly projectsPath?: string;
  readonly supportPath?: string;
  readonly getProjectPath?: (project: ClientProjectRow) => string;
  readonly getContractPath?: (contract: ClientSupportContractRow) => string;
  /** Destino del enlace "Volver". Solo lo pasa la vista previa de admin — en el
   *  portal real esta es la pantalla de entrada del cliente, sin página anterior. */
  readonly backTo?: string;
  readonly backLabel?: string;
  /** Título del header. Por defecto sigue la pestaña activa ("Proyectos"/"Soporte");
   *  la vista previa de admin lo fija en "Clientes", la sección del menú en que está. */
  readonly title?: string;
  /** Texto junto al nombre del cliente ("Vista cliente" en la vista previa de admin). */
  readonly companyNameSuffix?: string;
}

export function ClientPortalPage({
  clientId,
  projectsPath = '/portal/proyectos',
  supportPath = '/portal/soporte',
  getProjectPath = portalProjectPath,
  getContractPath = portalSupportContractPath,
  backTo,
  backLabel,
  title,
  companyNameSuffix,
}: ClientPortalPageProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab: 'proyectos' | 'soporte' = location.pathname.endsWith('/soporte') ? 'soporte' : 'proyectos';
  const { data: client } = useClientDetail(clientId ?? null);
  const company = client ? toClientCompanyInfo(client) : { name: '—', contactName: '—', contactPosition: '' };

  return (
    <DashboardLayout title={title ?? (activeTab === 'proyectos' ? 'Proyectos' : 'Soporte')}>
      <div className={styles.page}>
        {backTo && (
          <Link className={styles.breadcrumb} to={backTo}>
            <ChevronLeftIcon size={16} /> {backLabel ?? 'Volver'}
          </Link>
        )}

        <ClientCompanyHeader
          company={company}
          contractLabel={activeTab === 'proyectos' ? 'Contacto' : 'Responsable'}
          nameSuffix={companyNameSuffix}
        />

        <Tabs
          tabs={[
            { id: 'proyectos', label: 'Proyectos Desarrollo' },
            { id: 'soporte', label: 'Soporte' },
          ]}
          activeId={activeTab}
          onSelect={(id) => navigate(id === 'soporte' ? supportPath : projectsPath)}
        />

        {activeTab === 'proyectos' ? (
          <ClientProjectsSection clientId={clientId} getProjectPath={getProjectPath} />
        ) : (
          <ClientSupportSection clientId={clientId} getContractPath={getContractPath} />
        )}

        <div className={styles.footer}>
          <span>Diseñado y desarrollado por</span>
          <KabeliLogo className={styles.footerLogo} />
        </div>
      </div>
    </DashboardLayout>
  );
}

function ClientProjectsSection({
  clientId,
  getProjectPath,
}: {
  clientId: string | undefined;
  getProjectPath: (project: ClientProjectRow) => string;
}) {
  const { data } = useProjectsList({ clientId: clientId ?? undefined, pageSize: 100 });
  const projects = useMemo(() => (data?.items ?? []).map(toClientProjectRow), [data]);
  const [search, setSearch] = useState('');
  const activeCount = projects.filter((p) => p.health === 'active').length;
  const attentionCount = projects.filter((p) => p.health === 'on_hold').length;

  const query = search.trim().toLowerCase();
  const filtered = query
    ? projects.filter((p) => p.name.toLowerCase().includes(query) || p.leaderName.toLowerCase().includes(query))
    : projects;

  const columns: PortalTableColumn<ClientProjectRow>[] = [
    {
      key: 'name',
      header: 'Proyecto',
      sortValue: (p) => p.name,
      render: (project) => (
        <Link className={styles.projectLink} to={getProjectPath(project)}>
          {project.name}
        </Link>
      ),
    },
    { key: 'leader', header: 'Líder Kabeli', sortValue: (p) => p.leaderName, render: (p) => p.leaderName },
    { key: 'startDate', header: 'Inicio', sortValue: (p) => p.startDateIso, render: (p) => p.startDate },
    { key: 'endDate', header: 'Fin', sortValue: (p) => p.endDateIso, render: (p) => p.endDate ?? '--' },
    {
      key: 'health',
      header: 'Estado',
      sortValue: (p) => HEALTH_LABEL[p.health],
      render: (p) => <StatusPill tone={HEALTH_TONE[p.health]}>{HEALTH_LABEL[p.health]}</StatusPill>,
    },
    {
      key: 'stage',
      header: 'Etapa',
      sortValue: (p) => p.stageLabel,
      render: (p) => <span className={styles.stageLabel}>{p.stageLabel}</span>,
    },
    {
      key: 'progress',
      header: 'Avance',
      sortValue: (p) => p.progressPercent,
      // `null` = sin plan cargado: "--", no un 0% que parezca medido.
      render: (p) => (p.progressPercent != null ? `${p.progressPercent}%` : '--'),
    },
  ];

  return (
    <>
      <div className={styles.statsRow}>
        <StatPill value={projects.length} label="proyectos totales" />
        <StatPill value={activeCount} label="activos" />
        <StatPill value={attentionCount} label="con puntos de atención" />
        <TextField
          className={styles.search}
          type="search"
          placeholder="Buscar proyecto o líder"
          aria-label="Buscar proyecto"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          iconLeft={<SearchIcon size={16} />}
        />
      </div>

      <PortalTable columns={columns} items={filtered} getRowKey={(p) => p.id} />
    </>
  );
}

function ClientSupportSection({
  clientId,
  getContractPath,
}: {
  clientId: string | undefined;
  getContractPath: (contract: ClientSupportContractRow) => string;
}) {
  const { data } = useSupportPlansForClient(clientId ?? null);
  const contracts = useMemo(() => (data ?? []).map(toClientSupportContractRow), [data]);
  const [planFilter, setPlanFilter] = useState<SupportPlanType | null>(null);

  const countByPlan = useMemo(() => {
    const counts: Record<SupportPlanType, number> = { monthly: 0, bundle: 0, on_demand: 0 };
    for (const contract of contracts) counts[contract.planType] += 1;
    return counts;
  }, [contracts]);

  const filtered = planFilter ? contracts.filter((c) => c.planType === planFilter) : contracts;

  const columns: PortalTableColumn<ClientSupportContractRow>[] = [
    {
      key: 'name',
      // El nombre del contrato es un enlace en el Figma: lleva al detalle del
      // contrato de soporte (`ClientSupportContractDetailPage`) con su lista
      // de tickets — ver docs/DECISION-PORTAL-CLIENTE.md.
      header: 'Contrato',
      sortValue: (c) => c.name,
      render: (c) => (
        <Link className={styles.contractName} to={getContractPath(c)}>
          {c.name}
        </Link>
      ),
    },
    { key: 'kabeliOwner', header: 'Responsable Kabeli', sortValue: (c) => c.kabeliOwnerName, render: (c) => c.kabeliOwnerName },
    { key: 'planType', header: 'Tipo', sortValue: (c) => PLAN_TYPE_LABEL[c.planType], render: (c) => PLAN_TYPE_LABEL[c.planType] },
    {
      key: 'dates',
      header: 'Inicio - Término',
      sortValue: (c) => c.startDateIso,
      render: (c) => `${c.startDate} / ${c.endDate ?? '--'}`,
    },
    { key: 'contractedHours', header: 'N° horas', sortValue: (c) => c.contractedHours, render: (c) => c.contractedHours ?? '--' },
    { key: 'consumedHours', header: 'HH consumidas', sortValue: (c) => c.consumedHours, render: (c) => c.consumedHours != null ? `${c.consumedHours} UF` : '--' },
    { key: 'hourValue', header: 'Valor HH', sortValue: (c) => c.hourValueUf, render: (c) => c.hourValueUf != null ? formatMoney(c.hourValueUf, c.currency) : '--' },
    { key: 'billingState', header: 'Por facturar', sortValue: (c) => (c.billingState === '--' ? null : c.billingState), render: (c) => c.billingState },
    {
      key: 'status',
      header: 'Estado del contrato',
      sortValue: (c) => c.contractStatus,
      render: (c) => (
        <StatusPill tone={c.contractStatus === 'active' ? 'success' : 'neutral'}>
          {c.contractStatus === 'active' ? 'Vigente' : 'Finalizado'}
        </StatusPill>
      ),
    },
  ];

  return (
    <>
      <div className={styles.statsRow}>
        <StatPill value={countByPlan.bundle} label="Bolsa" onClick={() => setPlanFilter('bundle')} active={planFilter === 'bundle'} />
        <StatPill value={countByPlan.monthly} label="Mensual" onClick={() => setPlanFilter('monthly')} active={planFilter === 'monthly'} />
        <StatPill value={countByPlan.on_demand} label="A demanda" onClick={() => setPlanFilter('on_demand')} active={planFilter === 'on_demand'} />
        <StatPill value={contracts.length} label="Ver todos" onClick={() => setPlanFilter(null)} active={planFilter === null} />
      </div>

      <PortalTable columns={columns} items={filtered} getRowKey={(c) => c.id} />
    </>
  );
}
